/**
 * Intent URL helpers for progressive `/signup` (TP-502 Option A).
 *
 * When the user picks a role, put `type=seller` or `type=buyer` on the URL
 * (preserving UTMs and other params) so Mida can target only
 * `/signup?type=seller` for the Ari signup split-URL experiment.
 * Buyers use `type=buyer` and must not match that experiment.
 */

/**
 * @param {string} [search] location.search (with or without leading `?`)
 * @param {'seller'|'buyer'|null|undefined} accountType
 *   Pass null/undefined to remove `type` (e.g. back to role step).
 * @returns {string} search string including leading `?`, or `''` if empty
 */
export function buildSignupSearchWithAccountType(search, accountType) {
  const raw =
    search && search.charAt(0) === '?' ? search.slice(1) : search || '';
  const params = new URLSearchParams(raw);

  if (accountType === 'seller' || accountType === 'buyer') {
    params.set('type', accountType);
  } else {
    params.delete('type');
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Full path for history.replace / push.
 * @param {string} [pathname] defaults to `/signup`
 * @param {string} [search]
 * @param {'seller'|'buyer'|null|undefined} accountType
 */
export function buildSignupPathWithAccountType(pathname, search, accountType) {
  const path = pathname || '/signup';
  return `${path}${buildSignupSearchWithAccountType(search, accountType)}`;
}

/**
 * @param {string} [search]
 * @returns {'seller'|'buyer'|null}
 */
export function readSignupAccountTypeFromSearch(search) {
  const raw =
    search && search.charAt(0) === '?' ? search.slice(1) : search || '';
  const type = new URLSearchParams(raw).get('type');
  return type === 'seller' || type === 'buyer' ? type : null;
}

/**
 * Resolve progressive signup UI after browser Back/Forward (popstate).
 * Role select pushes `{ authSignupStep: 2 }`; Back clears URL type → step 1;
 * Forward must restore step 2 from that history state.
 *
 * @param {{
 *   historyState: ?{ authSignupStep?: number },
 *   currentStep: number,
 *   hasDefaultAccountType: boolean,
 *   urlAccountType: 'seller'|'buyer'|null,
 * }} args
 * @returns {null | {
 *   step: 1|2,
 *   type?: 'seller'|'buyer',
 *   clearUrlType: boolean,
 *   pushedStepHistory: boolean,
 * }}
 */
export function resolveSignupStepAfterPopState({
  historyState,
  currentStep,
  hasDefaultAccountType,
  urlAccountType,
}) {
  if (hasDefaultAccountType) {
    return null;
  }

  if (historyState && historyState.authSignupStep === 2) {
    const next = {
      step: 2,
      clearUrlType: false,
      pushedStepHistory: true,
    };
    if (urlAccountType === 'seller' || urlAccountType === 'buyer') {
      next.type = urlAccountType;
    }
    return next;
  }

  if (currentStep === 2) {
    return {
      step: 1,
      clearUrlType: true,
      pushedStepHistory: false,
    };
  }

  return null;
}
